import type {
   DocAiDocument,
   DocAiResponse,
} from '@/types/api-response/doc-ai-types';
import type { UrlSerializableFilterState } from '@/types/nmed/nmed-filter-types';
import type { MultiSelectFilterState, SearchKeyItem, SortDirection, SortOrder, YearFilterState } from '@/types/nmed/shared-filter-types';
import type { FetchError } from 'ofetch';
import type { ComprehensionCallback } from './comprehension/base';
import { useDocumentFilterState, useDocumentMetadata, useDocumentPagination } from '@/composables/nmed/shared';
import { useNmedDocSearchUndoHistory } from '@/composables/nmed/use-nmed-doc-search-undo-history';
import { useLDFlag } from '@/composables/use-ld-flag.ts';
import { fetchDocumentsByIds } from '@/services/document-client';
import { useAuthStore } from '@/stores/auth-store';
import { useMapUiStore } from '@/stores/v2/map-ui-store';
import { API_RETRY_CONFIG_EXTENDED } from '@/types/nmed/shared-filter-types';
import { removeItemFromArray } from '@/utils/array-utils';
import { areFiltersEqual } from '@/utils/filter-url-utils';
import { looksLikeFilename, parseUserQuery } from '@/utils/search-query-parser';
import { resolveSearchQuery } from '@/utils/search-query-resolver';
import { ofetch } from 'ofetch';
import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { comprehendDischargePermitPattern, comprehendDischargePermits } from './comprehension/discharge-permits';
import { comprehendGCP, comprehendModificationPermit, comprehendNOI, comprehendNSR, comprehendPSD } from './comprehension/permit-numbers';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Represents the state of search keyword filtering
 * Uses a grouped structure where each field maps to an array of values
 */
export interface SearchKeywordState {
   /** Grouped filters: { 'County': ['Bernalillo', 'Santa Fe'], 'FacilityName': ['Plant A'] } */
   filters: Record<string, string[]>;
}

/**
 * Represents coordinate-based filtering state
 */
export interface CordFilterState {
   lat: string;
   lng: string;
   radius: number;
}

/**
 * Represents year filter state
 */
export { type YearFilterState } from '@/types/nmed/shared-filter-types';

/**
 * Represents the sort order for document results
 */
export { type SortOrder } from '@/types/nmed/shared-filter-types';

/**
 * Represents the sort direction for table columns
 */
export { type SortDirection } from '@/types/nmed/shared-filter-types';

/**
 * Sort configuration for table-based document search
 */
export { type SortConfig } from '@/types/nmed/shared-filter-types';

/**
 * Doc-search specific filter state (fields not in sharedFilterState)
 */
export interface DocSearchLocalState {
   deepSearchKeyword: string;
   cord: CordFilterState;
   mapLocation: boolean;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                        CONSTANTS                                               */
/* ---------------------------------------------------------------------------------------------- */

const DEFAULT_ROWS_PER_PAGE = 30;
const MAP_RADIUS_MULTIPLIER = 0.75;
const PAGE_SIZE_STORAGE_KEY = 'doc-search-page-size';

/**
 * Get persisted page size from localStorage, or return default
 */
function getPersistedPageSize(): number {
   try {
      const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      if (stored) {
         const parsed = Number.parseInt(stored, 10);
         // Validate it's a reasonable page size
         if ([25, 50, 100, 200].includes(parsed)) {
            return parsed;
         }
      }
   }
   catch {
      // localStorage not available
   }
   return DEFAULT_ROWS_PER_PAGE;
}

/**
 * Persist page size to localStorage
 */
function persistPageSize(size: number): void {
   try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
   }
   catch {
      // localStorage not available
   }
}

/**
 * Pinia store for managing NMED document search filters and state
 */
export const useNmedDocSearchFilterStore = defineStore('docSearchFilter', () => {
   const authStore = useAuthStore();
   const mapUiStore = useMapUiStore();
   const useAstraEndpoint = useLDFlag('astra_search_experiment', false);
   const { mapCenterLocationCord, mapRadiusInMiles, markersLoadingState } = storeToRefs(mapUiStore);

   /* ------------------------------------------------------------------------------------------- */
   /*                              SHARED COMPOSABLES                                             */
   /* ------------------------------------------------------------------------------------------- */

   // Filter state (multi-select mode)
   const {
      filterState: sharedFilterState,
      isFilterApplied: sharedIsFilterApplied,
      getSearchFiltersAsArrays,
      setYear: setSharedYear,
      setSearchFilters: setSharedSearchFilters,
      toggleSearchFilterValue: sharedToggleSearchFilterValue,
      setGlobalSearchKeyword: setSharedGlobalSearchKeyword,
      clearAllFilters: clearSharedFilterState,
      clearSearchKeywords: sharedClearSearchKeywords,
   } = useDocumentFilterState();

   // Pagination & Sort (use persisted page size if available)
   const initialPageSize = getPersistedPageSize();
   const {
      pagination: sharedPagination,
      sortConfig,
      $reset: resetPaginationState,
   } = useDocumentPagination({ itemsPerPage: initialPageSize });

   /* ------------------------------------------------------------------------------------------- */
   /*                                         STATE                                               */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Abort controller for cancelling in-flight API requests
    */
   let abortController: AbortController | null = null;

   /**
    * Tracks whether Luma AI response documents have been added to the search results
    */
   const isLumaDocsAdded = ref<boolean>(false);

   /**
    * Chat ID associated with Luma AI document results
    */
   const lumaDocsChatId = ref<string>('');

   /**
    * Doc-search specific filter state (fields not shared with other stores)
    */
   const docSearchLocalState = reactive<DocSearchLocalState>({
      deepSearchKeyword: '',
      cord: {
         lat: '',
         lng: '',
         radius: 10,
      },
      mapLocation: false,
   });

   /**
    * Counter used to trigger force-clear of search keywords
    */
   const searchKeywordsForceClearCount = ref<number>(0);

   /**
    * State for document search results
    */
   const fetchDocumentsState = reactive({
      documents: [] as DocAiDocument[],
      numberOfDocuments: 0,
      error: false,
      isLoading: true,
   });

   /**
    * Doc-search specific pagination state for page-based navigation
    */
   const localPaginationState = reactive({
      firstRow: 0,
      numberOfRows: initialPageSize,
   });

   /**
    * Tracks whether the store has been initialized
    */
   const isInitialized = ref<boolean>(false);

   /**
    * Undo history for filter states
    */
   const undoHistory = useNmedDocSearchUndoHistory();

   /**
    * Track the last serializable state to avoid duplicate pushes
    */
   let lastSerializedState: UrlSerializableFilterState | null = null;

   /**
    * Stores the available search fields from the most recent API response.
    */
   const availableFieldsFromResponse = ref<string[]>([]);
   const bureausInResults = ref<string[]>([]);
   const docTypesInResults = ref<string[]>([]);

   // Metadata (derived from search response data)
   const {
      bureaus,
      documentTypes,
      searchFields,
      hasNoFilterOptions,
   } = useDocumentMetadata({
      responseBureaus: bureausInResults,
      responseDocTypes: docTypesInResults,
      responseSearchFields: availableFieldsFromResponse,
   });

   /* ------------------------------------------------------------------------------------------- */
   /*                                   COMPUTED - FILTER STATUS                                  */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Checks if any filter is currently applied
    */
   const isFilterApplied = computed(() => {
      return (
         sharedIsFilterApplied.value
         || docSearchLocalState.deepSearchKeyword !== ''
      );
   });

   /**
    * Checks if deep search (AI/semantic search) filter is active
    */
   const isDeepSearchFilterApplied = computed(() => {
      return docSearchLocalState.deepSearchKeyword !== '';
   });

   /**
    * Checks if global search (across all metadata) filter is active
    */
   const isGlobalSearchApplied = computed(() => {
      return sharedFilterState.globalSearchKeyword !== '';
   });

   /**
    * Checks if field-specific search keywords are applied
    */
   const isSearchKeywordApplied = computed(() => {
      return Object.keys(sharedFilterState.searchKeyword.filters).length > 0;
   });

   /**
    * Available bureaus from the search response.
    * Narrowing now happens server-side via the search response.
    */
   const relevantBureaus = computed(() => bureaus.value);

   /**
    * Detects filters that are no longer valid for the current Bureau/DocType selection.
    * Only flags as stale when valid options have loaded (length > 0).
    */
   const staleFilters = computed(() => {
      const staleDocTypes = sharedFilterState.documentTypes.filter(
         dt => documentTypes.value.length > 0 && !documentTypes.value.includes(dt),
      );
      const staleSearchFields = Object.keys(sharedFilterState.searchKeyword.filters).filter(
         field => searchFields.value.length > 0
            && !searchFields.value.includes(field)
            && (availableFieldsFromResponse.value.length === 0 || !availableFieldsFromResponse.value.includes(field)),
      );
      const staleBureaus = sharedFilterState.bureaus.filter(
         b => relevantBureaus.value.length > 0 && !relevantBureaus.value.includes(b),
      );
      return { bureaus: staleBureaus, documentTypes: staleDocTypes, searchFields: staleSearchFields };
   });

   /* ------------------------------------------------------------------------------------------- */
   /*                                        WATCHERS                                             */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Watches searchFields and resets sortField if it's no longer available
    */
   watch(searchFields, (newFields) => {
      if (sharedFilterState.sortField && !newFields.includes(sharedFilterState.sortField)) {
         sharedFilterState.sortField = '';
         sharedFilterState.sortOrder = '';
      }
   });

   /* ------------------------------------------------------------------------------------------- */
   /*                                      API FUNCTIONS                                          */
   /* ------------------------------------------------------------------------------------------- */

   /**
    * Fetches documents based on currently applied filters
    */
   async function fetchDocuments() {
      try {
         if (abortController) {
            abortController.abort();
         }

         abortController = new AbortController();

         // Clear Luma docs state when fetching new documents
         isLumaDocsAdded.value = false;
         lumaDocsChatId.value = '';

         fetchDocumentsState.isLoading = true;
         fetchDocumentsState.error = false;

         // Add permit markers to loading state
         if (!markersLoadingState.value.includes('permit')) {
            markersLoadingState.value.push('permit');
         }

         // Convert grouped filters to parallel arrays for API
         const { fields: searchFieldsList, values: searchKeysList } = getSearchFiltersAsArrays();

         const endpoint = useAstraEndpoint.value
            ? '/nm/nmed/agent/nmed_document_information_astra'
            : '/nm/nmed/agent/nmed_document_information';
         const response = await ofetch<DocAiResponse>(endpoint, {
            baseURL: import.meta.env.VITE_PUBLIC_API_BASE_URL,
            method: 'POST',
            retry: API_RETRY_CONFIG_EXTENDED.retries,
            retryDelay: API_RETRY_CONFIG_EXTENDED.retryDelay,
            retryStatusCodes: [...API_RETRY_CONFIG_EXTENDED.retryStatusCodes],
            signal: abortController.signal,
            body: {
               api_key: authStore.userApiKey?.api_key,
               bureau: sharedFilterState.bureaus.join(','),
               doc_type: sharedFilterState.documentTypes.join(','),
               year: sharedFilterState.year.year,
               year_from: sharedFilterState.year.startYear,
               year_to: sharedFilterState.year.endYear,
               search_fields_list: searchFieldsList,
               search_keys_list: searchKeysList,
               global_search_key: sharedFilterState.globalSearchKeyword,
               first_row: String(localPaginationState.firstRow),
               number_of_rows: String(localPaginationState.numberOfRows),
               target_lat: docSearchLocalState.mapLocation
                  ? mapCenterLocationCord.value.latitude.toFixed(6)
                  : '',
               target_lng: docSearchLocalState.mapLocation
                  ? mapCenterLocationCord.value.longitude.toFixed(6)
                  : '',
               radius: docSearchLocalState.mapLocation
                  ? Number((mapRadiusInMiles.value * MAP_RADIUS_MULTIPLIER).toFixed(2))
                  : 0,
               sort_field: sharedFilterState.sortField,
               sort_order: sharedFilterState.sortOrder,
            },
         });

         fetchDocumentsState.documents = response.documents;

         bureausInResults.value = response.bureau || [];
         docTypesInResults.value = response.doc_type || [];

         // Update pagination and document count
         localPaginationState.firstRow = localPaginationState.firstRow + response.documents.length;
         fetchDocumentsState.numberOfDocuments = Number(response.number_of_documents);

         // Sync total/filtered counts for shared pagination
         const totalDocs = Number(response.number_of_documents);
         sharedPagination.totalCount = totalDocs;
         sharedPagination.filteredCount = totalDocs;

         // Calculate total pages for page-based pagination
         sharedPagination.totalPages = Math.ceil(
            totalDocs / sharedPagination.itemsPerPage,
         );

         // Update available fields from API response and clear sort if no longer valid
         if (response.search_fields.length > 0) {
            availableFieldsFromResponse.value = response.search_fields;

            // Clear sort if the selected field is not in the available fields from API
            if (sharedFilterState.sortField && !response.search_fields.includes(sharedFilterState.sortField)) {
               sharedFilterState.sortField = '';
               sharedFilterState.sortOrder = '';
            }
         }

         fetchDocumentsState.isLoading = false;
         removeItemFromArray(markersLoadingState.value, 'permit');
      }
      catch (e) {
         // Silently ignore aborted requests (user initiated cancel)
         if (
            (e as FetchError).message?.includes('signal is aborted without reason')
            || (e as FetchError).message?.includes('The user aborted a request.')
         ) {
            return;
         }

         console.error('Failed to fetch documents:', e);
         fetchDocumentsState.error = true;

         fetchDocumentsState.isLoading = false;
         removeItemFromArray(markersLoadingState.value, 'permit');
      }
   }

   /**
    * Fetches all matching document IDs for the current filter state (up to a given limit).
    * Used for "Export All" ZIP when results span multiple pages.
    */
   async function fetchAllMatchingDocIds(limit = 100): Promise<string[]> {
      try {
         const { fields: searchFieldsList, values: searchKeysList } = getSearchFiltersAsArrays();

         const endpoint = useAstraEndpoint.value
            ? '/nm/nmed/agent/nmed_document_information_astra'
            : '/nm/nmed/agent/nmed_document_information';

         const response = await ofetch<DocAiResponse>(endpoint, {
            baseURL: import.meta.env.VITE_PUBLIC_API_BASE_URL,
            method: 'POST',
            retry: API_RETRY_CONFIG_EXTENDED.retries,
            retryDelay: API_RETRY_CONFIG_EXTENDED.retryDelay,
            retryStatusCodes: [...API_RETRY_CONFIG_EXTENDED.retryStatusCodes],
            body: {
               api_key: authStore.userApiKey?.api_key,
               bureau: sharedFilterState.bureaus.join(','),
               doc_type: sharedFilterState.documentTypes.join(','),
               year: sharedFilterState.year.year,
               year_from: sharedFilterState.year.startYear,
               year_to: sharedFilterState.year.endYear,
               search_fields_list: searchFieldsList,
               search_keys_list: searchKeysList,
               global_search_key: sharedFilterState.globalSearchKeyword,
               first_row: '0',
               number_of_rows: String(limit),
               target_lat: docSearchLocalState.mapLocation
                  ? mapCenterLocationCord.value.latitude.toFixed(6)
                  : '',
               target_lng: docSearchLocalState.mapLocation
                  ? mapCenterLocationCord.value.longitude.toFixed(6)
                  : '',
               radius: docSearchLocalState.mapLocation
                  ? Number((mapRadiusInMiles.value * MAP_RADIUS_MULTIPLIER).toFixed(2))
                  : 0,
               sort_field: sharedFilterState.sortField,
               sort_order: sharedFilterState.sortOrder,
            },
         });

         return response.documents.map(d => d.docid);
      }
      catch (e) {
         console.error('Failed to fetch all matching doc IDs:', e);
         return [];
      }
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                    FILTER SETTERS                                           */
   /* ------------------------------------------------------------------------------------------- */

   function setBureaus(bureaus: string[]) {
      sharedFilterState.bureaus = bureaus;
   }

   function setDocumentTypes(documentTypes: string[]) {
      sharedFilterState.documentTypes = documentTypes;
   }

   function setYear(year: Partial<YearFilterState>) {
      setSharedYear(year);
   }

   function setSearchFilters(filters: Record<string, SearchKeyItem[]>) {
      setSharedSearchFilters(filters);
   }

   function toggleSearchFilterValue(field: string, value: string, mode: 'ADD' | 'REMOVE') {
      sharedToggleSearchFilterValue(field, value, mode);
   }

   function setGlobalSearchKeyword(keyword: string) {
      setSharedGlobalSearchKeyword(keyword);
   }

   function setDeepSearchKeyword(keyword: string) {
      docSearchLocalState.deepSearchKeyword = keyword;
   }

   function setCord(cord: Partial<CordFilterState>) {
      docSearchLocalState.cord = {
         ...docSearchLocalState.cord,
         ...cord,
      };
   }

   function setMapLocation(mapLocation: boolean) {
      docSearchLocalState.mapLocation = mapLocation;
   }

   async function addLumaDocs(chatId: string, docs: Record<string, any>[]) {
      if (abortController) {
         abortController.abort();
      }

      if (fetchDocumentsState.isLoading) {
         fetchDocumentsState.isLoading = false;
      }

      softReset();

      isLumaDocsAdded.value = true;
      lumaDocsChatId.value = chatId;

      // Extract unique document IDs from references and fetch full documents
      const docIds = [...new Set(docs.map(d => d.document_id).filter(Boolean) as string[])];

      if (docIds.length > 0) {
         fetchDocumentsState.isLoading = true;
         try {
            const fullDocs = await fetchDocumentsByIds(docIds);
            fetchDocumentsState.documents = fullDocs;
         }
         catch {
            // Fall back to partial refs if fetch fails
            fetchDocumentsState.documents = docs as DocAiDocument[];
         }
         finally {
            fetchDocumentsState.isLoading = false;
         }
      }
      else {
         fetchDocumentsState.documents = docs as DocAiDocument[];
      }

      // Use the actual loaded document count for pagination — numberOfDocs or
      // reference.length may overcount when multiple references point to the
      // same document (different pages).
      const loadedCount = fetchDocumentsState.documents.length;
      fetchDocumentsState.numberOfDocuments = loadedCount;

      // Update pagination to reflect Luma docs (not the full corpus)
      sharedPagination.totalCount = loadedCount;
      sharedPagination.filteredCount = loadedCount;
      sharedPagination.totalPages = 1;
      sharedPagination.currentPage = 1;

      // Derive available fields from docs (fields with at least one non-null value)
      const internalKeys = new Set(['id']);
      const fields = new Set<string>();
      for (const doc of fetchDocumentsState.documents) {
         for (const [key, val] of Object.entries(doc)) {
            if (val != null && val !== '' && !internalKeys.has(key)) {
               fields.add(key);
            }
         }
      }
      availableFieldsFromResponse.value = [...fields];

      localPaginationState.firstRow = localPaginationState.firstRow + loadedCount;
   }

   function addLumaFilterOptions(filter: Record<string, any>[]) {
      softReset();

      const bureauFilter = filter.find(item => 'Bureau' in item);
      if (bureauFilter) {
         setBureaus([bureauFilter.Bureau]);
      }

      const docTypeFilter = filter.find(item => 'DocType' in item);
      if (docTypeFilter) {
         setDocumentTypes([docTypeFilter.DocType]);
      }

      const excludedKeys = ['Bureau', 'DocType'];
      const filters: Record<string, string[]> = {};

      filter.forEach((item) => {
         const [key, value] = Object.entries(item)[0];
         if (!excludedKeys.includes(key)) {
            if (!filters[key]) {
               filters[key] = [];
            }
            if (!filters[key].includes(value)) {
               filters[key].push(value);
            }
         }
      });

      if (Object.keys(filters).length > 0) {
         setSearchFilters(filters);
      }

      handleUpdateFilterDropdown();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                   FILTER HANDLERS                                           */
   /* ------------------------------------------------------------------------------------------- */

   async function clearAllFilters() {
      clearSharedFilterState();
      docSearchLocalState.deepSearchKeyword = '';
      docSearchLocalState.mapLocation = false;

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;

      searchKeywordsForceClearCount.value++;

      if (fetchDocumentsState.isLoading) {
         if (abortController) {
            abortController.abort();
         }
         fetchDocumentsState.isLoading = false;
         removeItemFromArray(markersLoadingState.value, 'permit');
      }

      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function handleUpdateFilterDropdown() {
      fetchDocumentsState.numberOfDocuments = 0;
      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;

      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function handleSortChange(field: string, order: SortOrder) {
      sharedFilterState.sortField = field;
      sharedFilterState.sortOrder = field ? order : '';

      sortConfig.field = field;
      sortConfig.direction = field ? (order.toUpperCase() as SortDirection) : 'ASC';

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   function setSortConfig(field: string, direction: SortDirection) {
      sortConfig.field = field;
      sortConfig.direction = direction;

      sharedFilterState.sortField = field;
      sharedFilterState.sortOrder = field ? (direction.toLowerCase() as SortOrder) : '';
   }

   async function handleGlobalSearchKeyword(keyword: string) {
      // Build metadata fields map from search response data
      const metadataFields: Record<string, string[]> = {};
      if (bureausInResults.value.length) {
         metadataFields.bureau = bureausInResults.value;
      }
      if (docTypesInResults.value.length) {
         metadataFields.doc_type = docTypesInResults.value;
      }

      // Parse and resolve the query against known metadata values
      const parsed = parseUserQuery(keyword);

      // Extract filename-like terms and route them to the filename field filter
      const filenameTerms = parsed.must.filter(looksLikeFilename);
      if (filenameTerms.length > 0) {
         parsed.must = parsed.must.filter(t => !looksLikeFilename(t));
         sharedFilterState.searchKeyword.filters.filename = filenameTerms;
      }

      const resolved = resolveSearchQuery(parsed, metadataFields);

      // Only replace filters for fields that had resolved matches

      if (resolved.matchedFields.bureau) {
         sharedFilterState.bureaus = resolved.matchedFields.bureau;
      }
      if (resolved.matchedFields.doc_type) {
         sharedFilterState.documentTypes = resolved.matchedFields.doc_type;
      }

      // Apply any other resolved fields to search keyword filters
      for (const [field, values] of Object.entries(resolved.matchedFields)) {
         if (field !== 'bureau' && field !== 'doc_type') {
            sharedFilterState.searchKeyword.filters[field] = values;
         }
      }

      // Set globalSearchKeyword to only the remaining unmatched terms
      setGlobalSearchKeyword(resolved.globalSearchKey);

      // Flush reactive updates so the search input syncs with the resolved keyword
      await nextTick();

      const newFilterState = await comprehendQueryChange();
      sharedFilterState.bureaus = newFilterState.bureaus;
      sharedFilterState.documentTypes = newFilterState.documentTypes;
      sharedFilterState.searchKeyword.filters = newFilterState.searchKeyword.filters;
      setGlobalSearchKeyword(newFilterState.globalSearchKeyword);

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();
      pushCurrentStateToHistory();
   }

   async function comprehendQueryChange(): Promise<MultiSelectFilterState> {
      const comprehensions: ComprehensionCallback[] = [comprehendDischargePermits, comprehendDischargePermitPattern, comprehendGCP, comprehendNOI, comprehendPSD, comprehendNSR, comprehendModificationPermit];
      let filterState = sharedFilterState;
      for (const comprehend of comprehensions) {
         filterState = await comprehend(filterState);
      }
      return filterState;
   }

   async function handleSearchFilters(filters: Record<string, SearchKeyItem[]>) {
      const currentFilters = sharedFilterState.searchKeyword.filters;
      if (
         Object.keys(currentFilters).length === 0
         && !sharedFilterState.globalSearchKeyword
         && !docSearchLocalState.deepSearchKeyword
         && Object.keys(filters).length === 0
      ) {
         return;
      }

      if (
         JSON.stringify(currentFilters) === JSON.stringify(filters)
         && !(sharedFilterState.globalSearchKeyword || docSearchLocalState.deepSearchKeyword)
      ) {
         return;
      }

      setGlobalSearchKeyword('');
      docSearchLocalState.deepSearchKeyword = '';

      setSearchFilters(filters);

      await handleUpdateFilterDropdown();
   }

   function clearSearchKeywords() {
      sharedClearSearchKeywords();
      docSearchLocalState.deepSearchKeyword = '';

      searchKeywordsForceClearCount.value++;
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                  SERIALIZATION & UNDO                                       */
   /* ------------------------------------------------------------------------------------------- */

   function getSerializableState(): UrlSerializableFilterState {
      const searchFilters: Record<string, SearchKeyItem[]> = {};
      for (const [field, values] of Object.entries(sharedFilterState.searchKeyword.filters)) {
         searchFilters[field] = [...values];
      }

      return {
         bureaus: [...sharedFilterState.bureaus],
         documentTypes: [...sharedFilterState.documentTypes],
         searchFilters,
         globalSearchKeyword: sharedFilterState.globalSearchKeyword,
         sortField: sharedFilterState.sortField,
         sortOrder: sharedFilterState.sortOrder,
         yearType: sharedFilterState.year.type,
         year: sharedFilterState.year.year,
         startYear: sharedFilterState.year.startYear,
         endYear: sharedFilterState.year.endYear,
      };
   }

   function applySerializedState(state: UrlSerializableFilterState, addToHistory: boolean = true): void {
      sharedFilterState.bureaus = [...(state.bureaus || [])];
      sharedFilterState.documentTypes = [...(state.documentTypes || [])];

      if (state.searchFilters && Object.keys(state.searchFilters).length > 0) {
         const filters: Record<string, SearchKeyItem[]> = {};
         for (const [field, values] of Object.entries(state.searchFilters)) {
            filters[field] = [...values];
         }
         sharedFilterState.searchKeyword.filters = filters;
      }
      else if (state.keywords && state.values && state.keywords.length > 0) {
         // Old format: migrate parallel arrays to grouped structure
         const filters: Record<string, string[]> = {};
         for (let i = 0; i < state.keywords.length; i++) {
            const field = state.keywords[i];
            const value = state.values[i];
            if (field && value) {
               if (!filters[field]) {
                  filters[field] = [];
               }
               if (!filters[field].includes(value)) {
                  filters[field].push(value);
               }
            }
         }
         sharedFilterState.searchKeyword.filters = filters;
      }
      else {
         sharedFilterState.searchKeyword.filters = {};
      }

      sharedFilterState.globalSearchKeyword = state.globalSearchKeyword;
      sharedFilterState.sortField = state.sortField;
      sharedFilterState.sortOrder = state.sortOrder;
      if (state.yearType) {
         sharedFilterState.year.type = state.yearType;
      }
      sharedFilterState.year.year = state.year;
      sharedFilterState.year.startYear = state.startYear;
      sharedFilterState.year.endYear = state.endYear;

      searchKeywordsForceClearCount.value++;

      if (addToHistory) {
         pushCurrentStateToHistory();
      }
      else {
         lastSerializedState = JSON.parse(JSON.stringify(getSerializableState()));
      }
   }

   function pushCurrentStateToHistory(): void {
      const currentState = getSerializableState();

      if (lastSerializedState && areFiltersEqual(currentState, lastSerializedState)) {
         return;
      }

      undoHistory.pushState(currentState);
      lastSerializedState = JSON.parse(JSON.stringify(currentState));
   }

   async function undoFilterChange(): Promise<boolean> {
      const previousState = undoHistory.undo();
      if (!previousState) {
         return false;
      }

      applySerializedState(previousState, false);

      localPaginationState.firstRow = 0;
      sharedPagination.currentPage = 1;
      await fetchDocuments();

      return true;
   }

   const canUndo = computed(() => undoHistory.canUndo.value);

   async function applyMetaAttributeFilter(
      fieldName: string,
      value: string,
      mode?: 'ADD' | 'ONLY' | 'REMOVE',
   ) {
      const filters = sharedFilterState.searchKeyword.filters;
      const valueExists = filters[fieldName]?.includes(value) ?? false;

      if (mode === 'REMOVE') {
         if (valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'REMOVE');
         }
         else {
            return;
         }
      }
      else if (mode === 'ONLY') {
         setGlobalSearchKeyword('');
         docSearchLocalState.deepSearchKeyword = '';
         sharedFilterState.searchKeyword.filters = { [fieldName]: [value] };

         await handleUpdateFilterDropdown();
         return;
      }
      else if (mode === 'ADD') {
         if (!valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'ADD');
         }
      }
      else {
         // Auto mode selection
         if (valueExists) {
            sharedToggleSearchFilterValue(fieldName, value, 'REMOVE');
         }
         else if (Object.keys(filters).length > 0) {
            sharedToggleSearchFilterValue(fieldName, value, 'ADD');
         }
         else {
            setGlobalSearchKeyword('');
            docSearchLocalState.deepSearchKeyword = '';
            sharedFilterState.searchKeyword.filters = { [fieldName]: [value] };
         }
      }

      await handleUpdateFilterDropdown();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                      PAGINATION                                             */
   /* ------------------------------------------------------------------------------------------- */

   async function navigateToPage(page: number) {
      if (page < 1 || page > sharedPagination.totalPages) {
         return;
      }

      sharedPagination.currentPage = page;
      localPaginationState.firstRow = (page - 1) * sharedPagination.itemsPerPage;

      await fetchDocuments();
   }

   /**
    * Change the number of items displayed per page
    * Resets to page 1 and refetches documents
    * Persists the preference to localStorage
    */
   async function changePageSize(newSize: number) {
      if (newSize === sharedPagination.itemsPerPage) {
         return;
      }

      sharedPagination.itemsPerPage = newSize;
      localPaginationState.numberOfRows = newSize;

      // Persist to localStorage
      persistPageSize(newSize);

      // Reset to first page
      sharedPagination.currentPage = 1;
      localPaginationState.firstRow = 0;

      await fetchDocuments();
   }

   /* ------------------------------------------------------------------------------------------- */
   /*                                  INITIALIZATION & RESET                                     */
   /* ------------------------------------------------------------------------------------------- */

   async function initialize(force: boolean = false) {
      if (isInitialized.value && !force) {
         return;
      }

      await fetchDocuments();

      isInitialized.value = true;

      pushCurrentStateToHistory();
   }

   function softReset() {
      if (abortController) {
         abortController.abort();
         abortController = null;
      }

      sharedFilterState.bureaus = [];
      sharedFilterState.documentTypes = [];
      sharedFilterState.sortField = '';
      sharedFilterState.sortOrder = '';
      clearSearchKeywords();
   }

   function $reset() {
      clearSharedFilterState();
      docSearchLocalState.deepSearchKeyword = '';
      docSearchLocalState.mapLocation = false;
      docSearchLocalState.cord = { lat: '', lng: '', radius: 10 };

      isLumaDocsAdded.value = false;
      lumaDocsChatId.value = '';

      searchKeywordsForceClearCount.value = 0;

      fetchDocumentsState.documents = [];
      fetchDocumentsState.numberOfDocuments = 0;
      fetchDocumentsState.error = false;
      fetchDocumentsState.isLoading = true;

      localPaginationState.firstRow = 0;
      localPaginationState.numberOfRows = DEFAULT_ROWS_PER_PAGE;
      sharedPagination.currentPage = 1;
      sharedPagination.itemsPerPage = DEFAULT_ROWS_PER_PAGE;
      sharedPagination.totalPages = 0;

      resetPaginationState();

      availableFieldsFromResponse.value = [];
      bureausInResults.value = [];
      docTypesInResults.value = [];

      isInitialized.value = false;

      if (abortController) {
         abortController.abort();
         abortController = null;
      }
   }

   return {
      // State - Shared (source of truth for filters and pagination)
      filterState: sharedFilterState,
      pagination: sharedPagination,
      // State - Doc-search specific
      docSearchLocalState,
      localPaginationState,
      // Other state
      searchKeywordsForceClearCount,
      fetchDocumentsState,
      sortConfig,
      isInitialized,

      // Getters - Filter
      isFilterApplied,
      isDeepSearchFilterApplied,
      isGlobalSearchApplied,
      isSearchKeywordApplied,
      staleFilters,

      isLumaDocsAdded,
      lumaDocsChatId,

      // Getters - Metadata
      bureaus,
      relevantBureaus,
      documentTypes,
      searchFields,
      availableFieldsFromResponse,
      bureausInResults,
      docTypesInResults,
      hasNoFilterOptions,

      // Actions - Fetch
      fetchDocuments,
      fetchAllMatchingDocIds,
      initialize,

      // Actions - Setters
      setBureaus,
      setDocumentTypes,
      setYear,
      setSearchFilters,
      toggleSearchFilterValue,
      setGlobalSearchKeyword,
      setDeepSearchKeyword,
      setCord,
      setMapLocation,
      addLumaDocs,
      addLumaFilterOptions,

      // Actions - Handlers
      clearAllFilters,
      handleUpdateFilterDropdown,
      handleSearchFilters,
      handleSortChange,
      handleGlobalSearchKeyword,
      setSortConfig,
      clearSearchKeywords,
      applyMetaAttributeFilter,

      // Actions - Pagination
      navigateToPage,
      changePageSize,

      // Actions - Serialization & Undo
      getSerializableState,
      applySerializedState,
      pushCurrentStateToHistory,
      undoFilterChange,
      canUndo,

      // Reset
      softReset,
      $reset,
   };
});
